import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import {
  AGENT_CATALOG,
  AgentEntityRef,
  anchorFamilyOf,
  entityRef,
  isKnownAnchor,
} from './agent-catalog';
import { AgentFormRegistry } from './agent-form.registry';

/** What a page reports about itself beyond its route. */
export interface AgentPageState {
  /** active tab or section when it is not in the URL */
  tab?: string | null;
  /** active filters, e.g. `{ template: 'alpha_tutor', period: '30d' }` */
  filters?: Record<string, AgentFilterValue>;
  /** objects the user has selected or opened in a detail panel */
  selection?: AgentEntityRef[];
}

export type AgentFilterValue = string | number | boolean | null;

/**
 * A report is the state itself, or a function read only when a message is sent —
 * for pages whose selection changes in many places (`() => ({ selection: … })`).
 */
export type AgentPageReport = AgentPageState | (() => AgentPageState);

/** Where the user is, read from the router and the UI catalog. */
export interface AgentPageRef {
  /** UI-catalog page id (`data.agent.page` on the route), null outside the dashboard */
  id: string | null;
  /** current path without query string */
  route: string;
  params: Record<string, string>;
  /** objects named by the route, e.g. the agent behind `/tutors/:tutorId/…` */
  entities: AgentEntityRef[];
  tab: string | null;
}

/**
 * The view the agent receives with every message. Data, never instructions:
 * the backend marks it as "reported by the browser" before the model sees it.
 * Contains ids and catalog names only — no field values, no page text.
 */
export interface AgentContextPayload {
  page: string | null;
  route: string;
  params: Record<string, string>;
  entities: AgentEntityRef[];
  tab: string | null;
  filters: Record<string, AgentFilterValue>;
  selection: AgentEntityRef[];
  /** catalog anchors rendered right now — what the user can actually see and use */
  anchors: string[];
  /** family anchors rendered right now, e.g. `{ setting: ['default_language', …] }` */
  anchor_families: Record<string, string[]>;
  /** catalog id of an open dialog (`dialog.*` anchor inside a dialog), if any */
  dialog: string | null;
  /** registered forms on screen (`[alAgentForm]`) */
  forms: string[];
}

export const AGENT_CONTEXT_LIMITS = {
  filters: 12,
  filterKey: 40,
  filterValue: 120,
  selection: 50,
  anchors: 60,
  familyValues: 80,
} as const;

const EMPTY_PAGE: AgentPageRef = { id: null, route: '/', params: {}, entities: [], tab: null };
const FILTER_KEY = /^[A-Za-z0-9_.-]+$/;

/**
 * Knows where the user is in the portal and what they can do there.
 *
 * Route part: every dashboard route carries `data: { agent: { page: '<id>' } }`
 * (checked by check-agent-contracts.mjs); the catalog page says which route
 * parameters name which objects and which one is the tab.
 *
 * Page part: pages report tab, filters and selection with `report(this, …)` —
 * a fixed state, or a function the snapshot calls (a page that throws is skipped).
 * Reports are released with the reporter's DestroyRef. Later reports win —
 * a detail component initialises after its page, so its selection beats the
 * page's (the rule AlphaCount uses).
 *
 * The full payload is built only when a message is sent (`snapshot()`), not on
 * every render.
 */
@Injectable({ providedIn: 'root' })
export class AgentContextService {
  private readonly router = inject(Router);
  private readonly forms = inject(AgentFormRegistry);
  private readonly pageSignal = signal<AgentPageRef>(EMPTY_PAGE);
  private readonly reports = new Map<object, AgentPageReport>();

  /** Current page, for UI that wants to react (start cards, help). */
  readonly page = this.pageSignal.asReadonly();

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => this.readRoute());
    this.readRoute();
  }

  /** A page or component reports what it shows. Pass its DestroyRef to release automatically. */
  report(owner: object, state: AgentPageReport, destroyRef?: DestroyRef): void {
    const isNew = !this.reports.has(owner);
    if (!isNew) this.reports.delete(owner); // re-insert: the latest report wins
    this.reports.set(owner, state);
    if (isNew && destroyRef) destroyRef.onDestroy(() => this.release(owner));
  }

  release(owner: object): void {
    this.reports.delete(owner);
  }

  /** The payload sent with a message. `root` is injectable for tests. */
  snapshot(root: ParentNode = document): AgentContextPayload {
    const page = this.pageSignal();
    const merged = this.mergedReports();
    const anchors = collectAnchors(root);
    return {
      page: page.id,
      route: page.route,
      params: { ...page.params },
      entities: page.entities,
      tab: merged.tab ?? page.tab,
      filters: capFilters(merged.filters),
      selection: (merged.selection ?? []).slice(0, AGENT_CONTEXT_LIMITS.selection),
      anchors: anchors.anchors,
      anchor_families: anchors.families,
      dialog: anchors.dialog,
      forms: this.forms.ids(),
    };
  }

  /** Re-reads page, parameters and entities from the router. Public for tests. */
  readRoute(): void {
    let snap: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    let pageId: string | null = null;
    const params: Record<string, string> = {};
    while (snap) {
      Object.assign(params, snap.params);
      const agent = snap.data?.['agent'];
      if (agent && typeof agent.page === 'string') pageId = agent.page;
      snap = snap.firstChild;
    }
    const catalogPage = pageId ? AGENT_CATALOG.pages[pageId] : undefined;
    if (pageId && !catalogPage) pageId = null;

    const entities: AgentEntityRef[] = [];
    for (const [param, type] of Object.entries(catalogPage?.params ?? {})) {
      const ref = entityRef(type, params[param]);
      if (ref) entities.push(ref);
    }
    const tabParam = catalogPage?.tab_param;
    this.pageSignal.set({
      id: pageId,
      route: (this.router.url || '/').split(/[?#]/)[0],
      params,
      entities,
      tab: tabParam ? params[tabParam] ?? null : null,
    });
  }

  private mergedReports(): AgentPageState {
    const merged: AgentPageState = { filters: {} };
    for (const report of this.reports.values()) {
      const state = readReport(report);
      if (state.tab !== undefined) merged.tab = state.tab;
      if (state.filters) Object.assign(merged.filters!, state.filters);
      if (state.selection && state.selection.length) merged.selection = state.selection;
    }
    return merged;
  }
}

function readReport(report: AgentPageReport): AgentPageState {
  if (typeof report !== 'function') return report;
  try {
    return report() ?? {};
  } catch {
    return {};
  }
}

function capFilters(filters: Record<string, AgentFilterValue> | undefined): Record<string, AgentFilterValue> {
  const out: Record<string, AgentFilterValue> = {};
  for (const [key, value] of Object.entries(filters ?? {})) {
    if (Object.keys(out).length >= AGENT_CONTEXT_LIMITS.filters) break;
    if (key.length > AGENT_CONTEXT_LIMITS.filterKey || !FILTER_KEY.test(key)) continue;
    if (value === null || typeof value === 'number' || typeof value === 'boolean') {
      out[key] = value;
    } else if (typeof value === 'string') {
      out[key] = value.slice(0, AGENT_CONTEXT_LIMITS.filterValue);
    }
  }
  return out;
}

function isRendered(el: Element): boolean {
  return el.getClientRects().length > 0;
}

/** Catalog anchors present and rendered under `root` — what the user really has in front of them. */
export function collectAnchors(root: ParentNode): {
  anchors: string[];
  families: Record<string, string[]>;
  dialog: string | null;
} {
  const anchors: string[] = [];
  const families: Record<string, string[]> = {};
  let dialog: string | null = null;

  const add = (id: string) => {
    const family = anchorFamilyOf(id);
    if (family && !AGENT_CATALOG.anchors[id]) {
      const values = (families[family.family] ??= []);
      if (values.length < AGENT_CONTEXT_LIMITS.familyValues && !values.includes(family.value)) {
        values.push(family.value);
      }
    } else if (anchors.length < AGENT_CONTEXT_LIMITS.anchors && !anchors.includes(id)) {
      anchors.push(id);
    }
  };

  root.querySelectorAll('[data-al-anchor]').forEach((el) => {
    const id = el.getAttribute('data-al-anchor') ?? '';
    if (!isKnownAnchor(id) || !isRendered(el)) return;
    if (id.startsWith('dialog.') && el.closest('nb-dialog-container')) dialog = id;
    add(id);
  });

  for (const [id, anchor] of Object.entries(AGENT_CATALOG.anchors)) {
    if (!anchor.selector) continue;
    const el = root.querySelector(anchor.selector);
    if (el && isRendered(el)) add(id);
  }
  return { anchors: anchors.sort(), families, dialog };
}
